import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { get } from 'api/utils';
import ENDPOINTS from 'api/utils/endpoints';
import useQueryKeys from 'api/utils/useQueryKeys';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import { ServicesFormFilterType } from 'pages/Campaign/CampaignCreation/utils/CampaignFiltersUIFormToServicesForm';
import { userAtom } from 'store';
import { assertIsDefined } from 'util/assert';

export type PerformanceMetrics =
  'totalShoppersDelivered' | 'netNewShoppersDelivered' | 'crmLeadOpportunities' | 'vinsSold';

interface FetchPerformanceResultsNotificationProps {
  token?: string;
  dealerId?: number;
}

export interface MostRecentCampaign {
  startDate: string;
  targetedVins: number;
  totalShoppersDelivered: number;
}

export interface PerformanceResultsNotification {
  name: string;
  id: number | string;
  chainId: number;
  key: string;
  startDate: string;
  endDate: string;
  targetedVins?: number;
  productLabel: string;
  productType?: string;
  description?: string | null;
  filters?: ServicesFormFilterType[] | null;
}

export interface PerformanceResultsNotificationData {
  campaignDetails: PerformanceResultsNotification[];
  revealedMetrics: {
    [key: string]: PerformanceMetrics[];
  };
  mostRecentCampaign: MostRecentCampaign;
  isInitialLogin?: boolean;
}

/**
 * usePerformanceResultsNotification
 * @param prop
 */

async function fetchPerformanceResultsNotification({
  token,
  dealerId,
}: FetchPerformanceResultsNotificationProps) {
  assertIsDefined(token);
  assertIsDefined(dealerId);

  return await get<PerformanceResultsNotificationData>(
    `${ENDPOINTS.performanceResultsNotification}/${dealerId}`,
    {
      headers: { token },
    },
  );
}

export default function usePerformanceResultsNotification() {
  const [dealerId] = useSelectedDealerId();
  const [user] = useAtom(userAtom);
  const { performanceResultsNotificationKey } = useQueryKeys();

  return useQuery({
    queryKey: performanceResultsNotificationKey,
    enabled: !!user?.token && !!dealerId,
    queryFn: () => fetchPerformanceResultsNotification({ token: user?.token, dealerId }),
  });
}
